import axiosInstance from 'api/axios';

interface APIResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface IGenerateImageFromTextRequestPayload {
  input: string;
  prompt_type: string;
  token?: string | undefined;
}

interface IGenerateImageFromTextResponse {
  image_url: string;
}

interface ISaveGeneratedImagePayload {
  input: string;
  prompt_type: string;
  img_url: string;
  file_name: string;
}

interface ISaveGeneratedImageResponse {
  data: string;
}

interface IRecentImagesResponse {
  created_at: string;
  id: number;
  img_url: string;
  prompt_input: string;
  prompt_type: string;
}

interface IUserImagesResponse {
  created_at: string;
  id: number;
  img_url: string;
  prompt_input: string;
  prompt_type: string;
}

export interface IUpdateImagePayload {
  input: string;
  prompt_type: string;
  token: string;
}

interface IEditImageNamePayload {
  name: string;
}

interface IEditImageNameResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    name: string;
    img_url: string;
  };
}

export const ImageGenService = {
  // Generate image from text
  generateImageFromText: async (
    payload: IGenerateImageFromTextRequestPayload
  ): Promise<APIResponse<IGenerateImageFromTextResponse>> => {
    const response = await axiosInstance.private.post(
      '/api/v1/text-to-image/generate',
      payload
    );
    return response.data;
  },

  // Save generated image
  saveGeneratedImage: async (
    payload: ISaveGeneratedImagePayload
  ): Promise<APIResponse<ISaveGeneratedImageResponse>> => {
    const response = await axiosInstance.private.post(
      '/api/v1/text-to-image/',
      payload
    );
    return response.data;
  },

  // Get all recent images
  getRecentImage: async (): Promise<APIResponse<IRecentImagesResponse[]>> => {
    const response = await axiosInstance.private.get(
      '/api/v1/text-to-image/recent'
    );
    return response.data;
  },

  // Get user images
  getUserImages: async (): Promise<APIResponse<IUserImagesResponse[]>> => {
    const response = await axiosInstance.private.get(
      '/api/v1/text-to-image/user'
    );
    return response.data;
  },

  // Update image
  updateImage: async (
    imageId: number,
    payload: IUpdateImagePayload
  ): Promise<APIResponse<string>> => {
    const response = await axiosInstance.private.put(
      `/api/v1/text-to-image/${imageId}`,
      payload
    );
    return response.data;
  },

  // Delete image
  deleteImage: async (imageId: number): Promise<APIResponse<string>> => {
    const response = await axiosInstance.private.delete(
      `/api/v1/gen-ai/delete-image?image_id=${imageId}`
    );
    return response.data;
  },

  // Generate image from random prompt
  generateImageFromRandomPrompt: async () => {
    const response = await axiosInstance.public.get(
      '/api/v1/text-to-image/random/prompt'
    );
    return response.data;
  },

  upscaleImage: async (payload: FormData, token: string) => {
    const response = await axiosInstance.private.post(
      '/api/v1/gen-ai/upscale',
      payload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          token: token,
        },
      }
    );
    return response.data;
  },

  removeObject: async (payload: FormData, token: string) => {
    const response = await axiosInstance.private.post(
      '/api/v1/gen-ai/object-remove',
      payload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          token: token,
        },
      }
    );
    return response.data;
  },

  replaceObject: async (payload: FormData, token: string) => {
    const response = await axiosInstance.private.post(
      '/api/v1/gen-ai/search-replace',
      payload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          token: token,
        },
      }
    );
    return response.data;
  },

  // Edit image name
  editImageName: async (
    imageId: number,
    payload: IEditImageNamePayload
  ): Promise<APIResponse<IEditImageNameResponse>> => {
    const response = await axiosInstance.private.put(
      `/api/v1/gen-ai/edit-image/${imageId}`,
      payload
    );
    return response.data;
  },

  // Download image
  downloadImage: async (imageId: number): Promise<Blob> => {
    const response = await axiosInstance.private.get(
      `/api/v1/gen-ai/download-image/${imageId}`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};
